<template>
    <div>
        <div class="flex justify-between mb-5 border-b border-gray-light pb-1">
            <div
                v-if="showHeader"
                class="flex"
                :class="[smallHeader ? 'my-3' : 'mb-5']"
            >
                <h1 class="page-header" :class="{ 'text-2xl': smallHeader }">
                    Users
                </h1>
                <div class="ml-3 flex items-center text-gray-600">
                    <div :class="[smallHeader ? 'text-lg' : 'text-2xl']">/</div>
                    <div
                        class="ml-2 text-xl"
                        :class="[smallHeader ? 'pt-2px' : 'pt-1']"
                    >
                        <template v-if="showFilteredCount && totalAllowedUsers > 0">
                            {{ users.filtered.length }} of {{ users.all.length }} (filtered from {{ totalAllowedUsers }})
                        </template>
                        <template v-else-if="totalAllowedUsers > 0">
                            {{ users.all.length }} of {{ totalAllowedUsers }}
                        </template>
                        <template v-else>
                            {{ users.filtered.length }} of {{ users.all.length }}
                        </template>
                    </div>
                </div>
            </div>
            <div v-if="showFilters" class="flex items-center">
                <filters
                    data-type="users"
                    sort-by="name"
                    :show-client-filter="false"
                    :selected-view-option="layout"
                    @view-change="layout = $event"
                >
                </filters>
                <button
                    v-if="$gate.isAdmin() || $gate.isClientAdmin()"
                    @click="handleAddEntity('users', 'manage-users')"
                    type="button"
                    class="ml-4 flex items-center btn btn-small btn-blue focus:outline-none"
                >
                    <inline-svg name="edit-white"></inline-svg>
                    Manage Users
                </button>
            </div>
        </div>

        <div class="flex flex-wrap justify-between" v-if="layout == 'grid'">
            <div class="grid grid-cols-3 gap-8">
                <div class="bg-gray-50 shadow" v-for="user in users.filtered" :key="user.id">
                    <div v-if="$gate.isAdmin() || $gate.isClientAdmin()" class="relative">
                        <div
                            @click="editUser(user.id)"
                            class="absolute mr-3 mt-3 right-0 top-0"
                        >
                            <inline-svg name="edit"></inline-svg>
                        </div>
                    </div>
                    <div class="box border h-full">
                        <div class="flex w-full h-full">
                            <div class="w-64 hidden">
                                <img
                                    v-if="user.avatar"
                                    class="w-full h-full"
                                    :src="user.avatar.view_url"
                                />
                                <img
                                    v-if="!user.avatar"
                                    class="w-full h-full"
                                    src="/img/temp/blank-avatar.jpg"
                                />
                            </div>
                            <div class="flex flex-1 flex-col p-4">
                                <div class="flex-1">
                                    <div>
                                        <p
                                            class="text-xl text-blue-500 font-bold"
                                        >
                                            {{ user.name }}
                                        </p>
                                        <p class="text-sm">
                                            {{ user.role.label }}
                                        </p>
                                    </div>
                                    <div class="mt-6">
                                        <p class="text-xl">
                                            {{ user.department }}
                                        </p>
                                        <p class="text-base">
                                            {{ user.title }}
                                        </p>
                                    </div>
                                </div>
                                <div class="mt-5 text-base">
                                    <div
                                        v-if="user.cell"
                                        class="flex items-center mb-1"
                                    >
                                        <inline-svg name="phone"></inline-svg>
                                        <span>{{ user.cell }}</span>
                                    </div>
                                    <div
                                        v-if="user.phone && !user.cell"
                                        class="flex items-center mb-1"
                                    >
                                        <inline-svg name="phone"></inline-svg>
                                        <span>{{ user.phone }}</span>
                                        <span v-if="user.extension" class="ml-2"
                                            >x{{ user.extension }}</span
                                        >
                                    </div>
                                    <div class="flex items-center">
                                        <inline-svg name="email"></inline-svg>
                                        <span
                                            ><a
                                                :href="'mailto:' + user.email"
                                                >{{ user.email }}</a
                                            ></span
                                        >
                                    </div>
                                    <div class="flex items-center" v-if="$gate.isAdmin() || $gate.isClientAdmin()">
                                        <div
                                            class="flex items-center rounded mb-1 mt-1 px-2 py-1"
                                            :class="{
                                                'bg-green-100 text-green-800': user.is_2fa_enabled,
                                                'bg-red-100 text-red-800': !user.is_2fa_enabled
                                            }"
                                        >
                                            <inline-svg name="shield" :class="{
                                                'w-4 h-4 fill-current': true,
                                                'text-green-600': user.is_2fa_enabled,
                                                'text-red-600': !user.is_2fa_enabled
                                            }"></inline-svg>
                                            <span class="px-2 py-1 text-xs font-medium">
                                            {{ user.two_fa_status }}
                                            </span>
                                        </div>
                                        <span v-if="user.is_2fa_enabled && user.two_fa_method !== null">
                                            <a
                                            href="#"
                                            @click.prevent="sendRecoveryCode(user.id)"
                                            class="px-2 py-1 ml-1 text-xs font-medium"
                                            :class="{
                                                'opacity-50 cursor-not-allowed':
                                                    loadingUserId ===
                                                    user.id,
                                            }"
                                            >
                                            <span
                                                v-if="loadingUserId !== user.id"
                                                >Send Recovery Code</span
                                            >
                                            <span v-else>Sending...</span>
                                            </a>
                                        </span>
                                        </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="layout == 'table' && users.filtered.length">
            <table class="w-full">
                <thead class="cursor-pointer">
                    <th></th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Cell</th>
                    <th>Office</th>
                    <th>Email</th>
                    <th>Authentication</th>
                    <th v-if="$gate.isAdmin() || $gate.isClientAdmin()"></th>
                </thead>
                <tbody>
                    <tr v-for="user in users.filtered" :key="user.id">
                        <td>
                            <div class="flex justify-center px-2">
                                <img
                                    v-if="user.avatar"
                                    class="w-8 h-8 rounded-full shadow-md"
                                    :src="user.avatar.view_url"
                                />
                                <inline-svg
                                    v-if="!user.avatar"
                                    name="user"
                                ></inline-svg>
                            </div>
                        </td>
                        <td class="leading-normal">
                            <p class="text-base font-medium">{{ user.name }}</p>
                            <p>{{ user.role.label }}</p>
                        </td>
                        <td class="leading-normal">
                            <p class="text-sm">{{ user.department }}</p>
                            <p>{{ user.title }}</p>
                        </td>
                        <td>{{ user.cell }}</td>
                        <td>
                            {{ user.phone }}
                            <span v-if="user.extension"
                                >x{{ user.extension }}</span
                            >
                        </td>
                        <td>
                            <a
                                class="text-sm font-medium"
                                :href="'mailto:' + user.email"
                                >{{ user.email }}</a
                            >
                        </td>
                        <td>
                            <span
                                :class="{
                                    'inline-block px-2 py-1 rounded text-xs font-medium': true,
                                    'bg-green-100 text-green-800': user.is_2fa_enabled && user.two_fa_method,
                                    'bg-yellow-100 text-yellow-800': user.is_2fa_enabled && !user.two_fa_method,
                                    'bg-red-100 text-red-800': !user.is_2fa_enabled
                                }"
                            >
                                {{ user.two_fa_status }}
                            </span>
                        </td>
                        <td v-if="$gate.isAdmin() || $gate.isClientAdmin()">
                            <div class="flex justify-end">
                                <div
                                    @click="editUser(user.id)"
                                    title="Edit User"
                                >
                                    <inline-svg name="edit"></inline-svg>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="!this.users.filtered.length">
            <p class="none-found">No Users Found</p>
        </div>

        <modal
            name="manage-users"
            width="60%"
            height="auto"
            :clickToClose="true"
            :scrollable="true"
            draggable=".modal-header"
            @closed="onModalClosed"

        >
            <manage-users :clientId="clients.selectedClient.id"></manage-users>
        </modal>

        <modal
            name="edit-user"
            width="60%"
            height="auto"
            :clickToClose="true"
            :scrollable="true"
            draggable=".modal-header"
        >
            <manage-users
                :clientId="selectedUser.client_id"
                :userId="selectedUser.id"
            >
            </manage-users>
        </modal>
    </div>
</template>

<script>
import { mapState } from "vuex";

export default {
    props: {
        userData: {
            type: Array,
            required: true,
        },

        showHeader: {
            type: Boolean,
            default: true,
        },

        smallHeader: {
            type: Boolean,
            default: false,
        },

        showFilters: {
            type: Boolean,
            default: true,
        },

        view: {
            type: String,
            default: "grid",
        },
    },

    data() {
        return {
            selectedUserId: "",
            layout: this.view,
            loadingUserId: null,
            moduleName: 'User',

        };
    },

    mounted() {
        if (localStorage.getItem("reopt_users_layout")) {
            this.layout = localStorage.getItem("reopt_users_layout");
        }

        this.users.all = this.userData;
        this.users.filtered = this.userData;

        this.$emit("filterRequest");
    },

    watch: {
        userData() {
            this.users.all = this.userData;
            this.users.filtered = this.userData;

            this.$emit("filterRequest");
        },
    },

    computed: {
        ...mapState(["clients"]),
        ...mapState(["users"]),
        
        showFilteredCount() {
            // Show filtered count when filters are applied, regardless of whether there are results
            return this.users.filtered.length !== this.users.all.length;
        },
        
        totalAllowedUsers() {
            // Get the module data for users
            const moduleData = this.$store.getters['moduleAccess/getModuleBySlug']('users');
            return moduleData?.subscription_details?.quantity || 0;
        },

        selectedUser() {
            return _.find(this.users.all, ["id", this.selectedUserId]) || {};
        },
    },

    methods: {
        onModalClosed() {
            const index = this.users.all.findIndex(user => user.id === 'new');
            if (index !== -1) {
                this.users.all.splice(index, 1);
            }

        },
        editUser(id) {
            this.selectedUserId = id;
            this.$modal.show("edit-user");
        },
        // Open modal to confirm sending recovery code
        sendRecoveryCode(id) {
            this.$swal({
                title: "Are you sure?",
                text: "Are you sure you want to send a recovery code to this user?",
                type: "success",
                buttonsStyling: false,
                confirmButtonText: "Confirm",
                confirmButtonClass: "btn btn-green",
                showCancelButton: true,
                cancelButtonClass: "btn btn-secondary mr-4",
                reverseButtons: true,
            }).then((result) => {
                if (result.value) {
                    this.confirmSendRecoveryCode(id);
                }
            });
        },
        // Called after user confirms in modal
        async confirmSendRecoveryCode(id) {
            this.loadingUserId = id; // start loader
            
            try {
                const response = await axios.post(`/users/${id}/send-recovery-code`);
                this.$notify({
                    type: "success",
                    title: "Success",
                    text: response.data.message,
                });
            } catch (error) {
                this.$notify({
                    type: "error",
                    title: "Error",
                    text: error.response?.data?.message || "Failed to send recovery code.",
                });
            } finally {
                this.loadingUserId = null; // stop loader
            }
        },
        handleAddEntity(moduleSlug, newEntityModalName) {
            console.log('Opening module access overlay for:', moduleSlug, newEntityModalName);
            // Use the global module access overlay with explicit module name
            this.$moduleAccessOverlay.show(moduleSlug, {
                targetModal: newEntityModalName,
                forceUpdate: true
            });
        },
    },
};
</script>
